import { useState, useEffect, ReactElement } from "react";
import styled from "styled-components";
import { primaryActive, primaryInactive } from "../../typography/alpha";

const StyledNavbar = styled.div`
  display: inline-flex;
  width: 100%;
  padding-left: 8px;
  border-bottom: 1px solid var(--color-primary-100);
  max-height: 40px;
  box-sizing: border-box;
`;

const Tab = styled.button`
  position: relative;
  cursor: pointer;
  box-sizing: border-box;
  min-height: 32px;
  line-height: 16px;
  margin: 0px 12px;
  overflow: hidden;
  padding: 12px 0px 8px 0px;
  text-align: center;
  ${primaryInactive.body1};
  color: var(--color-primary-800);
  background: var(--color-neutral-000-full);
  border: none;
  border-bottom: 4px solid var(--color-neutral-000-full);
  :focus {
    outline: none;
  }
  :hover,
  :focus {
    &:after {
      transform: scaleX(1);
    }
  }
`;

const ActiveTab = styled(Tab)`
  cursor: default;
  ${primaryActive.subtitle3};
  border-bottom: 4px solid var(--color-secondary-800);
`;

type CustomTabProps = {
  id: string;
  component: ReactElement | ((props: { tabIsActive: boolean }) => ReactElement);
};

type TabProps = { label: string; onClick: () => void } | CustomTabProps;

type SubNavbarProps = {
  tabs: TabProps[];
  index?: number;
};

export const SubNavbar = (props: SubNavbarProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState(
    props.index ? props.index : 0
  );
  useEffect(() => {
    setActiveTabIndex(props.index ? props.index : 0);
  }, [props.index]);

  return (
    <StyledNavbar>
      {props.tabs.map((tab, index) => {
        const tabIsActive = index === activeTabIndex;
        const TabComponent = tabIsActive ? ActiveTab : Tab;
        const onClick = tabIsActive
          ? () => {}
          : () => {
              setActiveTabIndex(index);
              if ("onClick" in tab) tab.onClick();
            };
        const tabContent =
          "label" in tab
            ? tab.label
            : typeof tab.component === "function"
            ? tab.component({
                tabIsActive,
              })
            : tab.component;
        return (
          <TabComponent
            data-testid={`sub-navbar-tab-${
              "label" in tab ? tab.label : tab.id
            }`}
            key={"label" in tab ? tab.label : tab.id}
            onClick={onClick}
            tabIndex={index}
          >
            {tabContent}
          </TabComponent>
        );
      })}
    </StyledNavbar>
  );
};
